import type { LyricLine, LyricWord, TTMLLyric } from "$/types/ttml";

export const REVIEW_NS = "urn:syllasync:review:1";
export type ReviewField = "begin" | "end" | "text";
export type ReviewStatus =
	| "unreviewed"
	| "confirmed"
	| "corrected"
	| "uncertain";
export interface ReviewValue {
	text: string;
	begin: number;
	end: number;
	background: boolean;
	ruby?: { word: string; startTime: number; endTime: number }[];
}
export interface Confidence {
	level: "high" | "low" | "unknown";
	reasons: string[];
}
export interface ReviewEntry {
	status: ReviewStatus;
	fields: ReviewField[];
	value: ReviewValue;
	reason: string;
	note: string;
	reviewer: string;
	at: string;
}
export interface ReviewDocument {
	version: 1;
	generation: { id: string; audio_sha256: string; [key: string]: unknown };
	originals: Record<
		string,
		{
			value: ReviewValue;
			confidence: { begin: Confidence; end: Confidence };
			[key: string]: unknown;
		}
	>;
	reviews: Record<string, ReviewEntry>;
	[key: string]: unknown;
}

const object = (v: unknown): v is Record<string, unknown> =>
	!!v && typeof v === "object" && !Array.isArray(v);
const validValue = (v: unknown): v is ReviewValue =>
	object(v) &&
	typeof v.text === "string" &&
	typeof v.background === "boolean" &&
	Number.isSafeInteger(v.begin) &&
	Number.isSafeInteger(v.end) &&
	(v.begin as number) >= 0 &&
	(v.end as number) >= (v.begin as number);
export function validateReview(
	value: unknown,
): asserts value is ReviewDocument {
	if (!object(value) || value.version !== 1)
		throw new Error("unsupported_review_version");
	if (
		!object(value.generation) ||
		typeof value.generation.id !== "string" ||
		typeof value.generation.audio_sha256 !== "string" ||
		!/^[0-9a-f]{64}$/.test(value.generation.audio_sha256)
	)
		throw new Error("invalid_review_generation");
	if (!object(value.originals) || !object(value.reviews))
		throw new Error("invalid_review_records");
	for (const [id, original] of Object.entries(value.originals)) {
		if (
			!id ||
			!object(original) ||
			!validValue(original.value) ||
			!object(original.confidence)
		)
			throw new Error("invalid_review_original");
		for (const field of ["begin", "end"]) {
			const c = original.confidence[field];
			if (
				!object(c) ||
				!["high", "low", "unknown"].includes(String(c.level)) ||
				!Array.isArray(c.reasons) ||
				!c.reasons.every((r) => typeof r === "string")
			)
				throw new Error("invalid_review_confidence");
		}
	}
	for (const [id, r] of Object.entries(value.reviews)) {
		if (
			!id ||
			!object(r) ||
			!["unreviewed", "confirmed", "corrected", "uncertain"].includes(
				String(r.status),
			) ||
			!validValue(r.value) ||
			!Array.isArray(r.fields) ||
			new Set(r.fields).size !== r.fields.length ||
			!r.fields.every((f) => ["begin", "end", "text"].includes(f)) ||
			!["reason", "note", "reviewer", "at"].every(
				(k) => typeof r[k] === "string",
			)
		)
			throw new Error("invalid_review_entry");
	}
}

export function wordValue(word: LyricWord, line: LyricLine): ReviewValue {
	return {
		text: word.word.trimEnd(),
		begin: Math.round(word.startTime),
		end: Math.round(word.endTime),
		background: line.isBG,
		...(word.ruby?.length
			? {
					ruby: word.ruby.map((r) => ({
						word: r.word,
						startTime: r.startTime,
						endTime: r.endTime,
					})),
				}
			: {}),
	};
}
export function sameValue(a: ReviewValue, b: ReviewValue): boolean {
	return (
		a.text === b.text &&
		a.begin === b.begin &&
		a.end === b.end &&
		a.background === b.background &&
		JSON.stringify(a.ruby ?? []) === JSON.stringify(b.ruby ?? [])
	);
}
export function effectiveReview(
	doc: ReviewDocument,
	word: LyricWord,
	line: LyricLine,
): ReviewEntry | undefined {
	const review = doc.reviews[word.id];
	return review && sameValue(review.value, wordValue(word, line))
		? review
		: undefined;
}
export function wordPriority(
	doc: ReviewDocument,
	word: LyricWord,
	field?: "begin" | "end",
): "high" | "low" | "unknown" {
	const sources = word.sourceIds ?? [];
	if (!sources.length) return "unknown";
	const levels = sources.flatMap((id) => {
		const c = doc.originals[id]?.confidence;
		return c
			? field
				? [c[field].level]
				: [c.begin.level, c.end.level]
			: ["unknown"];
	});
	return levels.includes("low")
		? "low"
		: levels.includes("unknown")
			? "unknown"
			: "high";
}
export function recordReview(
	lyric: TTMLLyric,
	ids: Set<string>,
	fields: ReviewField[],
	status: "confirmed" | "uncertain" | "unreviewed",
	reason: string,
	note: string,
	reviewer: string,
): TTMLLyric {
	if (!lyric.review) return lyric;
	if (status === "confirmed" && (!reviewer.trim() || !fields.length))
		throw new Error("reviewer_and_fields_required");
	const reviews: Record<string, ReviewEntry> = Object.assign(
		Object.create(null),
		lyric.review.reviews,
	);
	for (const line of lyric.lyricLines)
		for (const word of line.words) {
			if (!ids.has(word.id)) continue;
			const original =
				word.sourceIds?.length === 1
					? lyric.review.originals[word.sourceIds[0]]
					: undefined;
			const value = wordValue(word, line);
			reviews[word.id] = {
				status:
					status === "confirmed" &&
					(!original || !sameValue(original.value, value))
						? "corrected"
						: status,
				fields:
					status === "confirmed" &&
					["confirmed", "corrected"].includes(
						effectiveReview(lyric.review, word, line)?.status ?? "",
					) &&
					effectiveReview(lyric.review, word, line)?.reviewer ===
						reviewer.trim()
						? [
								...new Set([
									...(effectiveReview(lyric.review, word, line)?.fields ?? []),
									...fields,
								]),
							]
						: [...fields],
				value,
				reason,
				note,
				reviewer: reviewer.trim(),
				at: new Date().toISOString(),
			};
		}
	return { ...lyric, review: { ...lyric.review, reviews } };
}

export function feedbackPayload(lyric: TTMLLyric) {
	const doc = lyric.review;
	if (!doc) throw new Error("missing_review_metadata");
	return {
		...doc,
		current: lyric.lyricLines.flatMap((line, lineIndex) =>
			line.words
				.filter((w) => w.word.trim())
				.map((word) => ({
					id: word.id,
					source_ids: word.sourceIds ?? [],
					line_index: lineIndex,
					value: wordValue(word, line),
					review: effectiveReview(doc, word, line) ?? null,
				})),
		),
	};
}

export function readReview(doc: Document): ReviewDocument | undefined {
	const nodes = doc.getElementsByTagNameNS(REVIEW_NS, "review");
	if (!nodes.length) return undefined;
	if (nodes.length !== 1) throw new Error("duplicate_review_metadata");
	const value: unknown = JSON.parse(nodes[0].textContent ?? "");
	validateReview(value);
	return value;
}
export function readSources(element: Element): string[] | undefined {
	const attr = element.getAttributeNS(REVIEW_NS, "sources");
	if (attr === null) return undefined;
	const value: unknown = JSON.parse(attr);
	if (!Array.isArray(value) || !value.every((s) => typeof s === "string"))
		throw new Error("invalid_review_sources");
	return value;
}
export function validateLinks(lyric: TTMLLyric): void {
	const doc = lyric.review;
	if (!doc) return;
	const ids = new Set<string>();
	for (const line of lyric.lyricLines)
		for (const word of line.words) {
			if (!word.word.trim()) continue;
			if (ids.has(word.id)) throw new Error("duplicate_review_id");
			ids.add(word.id);
			if (word.sourceIds?.some((id) => !Object.hasOwn(doc.originals, id)))
				throw new Error("unknown_review_source");
		}
}
